import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['banner', 'button', 'iosInstructions', 'androidInstructions']

  DISMISSAL_KEY = 'pwa-install-dismissed'
  DISMISSAL_DURATION = 7 * 24 * 60 * 60 * 1000 // 1 week
  ANDROID_CHECK_INTERVAL = 500
  ANDROID_CHECK_TIMEOUT = 10000
  SHOW_DELAY_IOS = 2000
  SHOW_DELAY_ANDROID = 3000

  connect() {
    try {
      this.deferredPrompt = null
      this.isIOS = this.detectIOS()
      this.isAndroid = this.detectAndroid()
      this.isStandalone = this.detectStandalone()

      if (this.isStandalone) {
        // Clear caches when running in standalone mode to prevent authentication issues
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_CACHE'
          });
        }
        return this.hidePrompt()
      }

      this.setupEventListeners()
      if (this.shouldShowPrompt()) {
        this.schedulePromptDisplay()
      }
    } catch (error) {
      console.warn('[PWA Install] Error in connect, continuing without PWA features:', error);
      // Continue without PWA functionality rather than breaking the app
    }
  }

  disconnect() {
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', this.handleAppInstalled)
  }

  // ========== Platform Detection ==========

  detectIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }

  detectAndroid() {
    return /Android/.test(navigator.userAgent)
  }

  detectStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone ||
           document.referrer.includes('android-app://')
  }

  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768 && 'ontouchstart' in window)
  }

  // ========== Event Handlers ==========

  setupEventListeners() {
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt.bind(this))
    window.addEventListener('appinstalled', this.handleAppInstalled.bind(this))

    if (this.isAndroid) {
      this.setupAndroidButtonWatcher()
    }
  }

  handleBeforeInstallPrompt(event) {
    event.preventDefault()
    this.deferredPrompt = event
    this.showPromptForPlatform('android')
    this.showInstallButton()
  }

  handleAppInstalled() {
    this.hidePrompt()
  }

  async install(event) {
    event.preventDefault()
    if (!this.deferredPrompt) return

    this.deferredPrompt.prompt()
    const { outcome } = await this.deferredPrompt.userChoice

    this.deferredPrompt = null
    if (outcome === 'accepted') {
      this.hidePrompt()
    }
  }

  dismiss() {
    localStorage.setItem(this.DISMISSAL_KEY, Date.now().toString())
    this.hidePrompt()
  }

  // ========== Prompt Display Logic ==========

  shouldShowPrompt() {
    if (!this.isMobileDevice()) return false

    const dismissedTime = parseInt(localStorage.getItem(this.DISMISSAL_KEY) || '0', 10)
    return dismissedTime <= Date.now() - this.DISMISSAL_DURATION
  }

  schedulePromptDisplay() {
    const delay = this.isIOS ? this.SHOW_DELAY_IOS : this.SHOW_DELAY_ANDROID
    const platform = this.isIOS ? 'ios' : 'android'

    setTimeout(() => this.showPromptForPlatform(platform), delay)
  }

  showPromptForPlatform(platform) {
    if (!this.hasBannerTarget) return

    this.showBanner()

    if (platform === 'ios') {
      this.showElement(this.iosInstructionsTarget)
      this.hideElement(this.androidInstructionsTarget)
    } else {
      this.showElement(this.androidInstructionsTarget)
      this.hideElement(this.iosInstructionsTarget)
      this.updateInstallButtonVisibility()
    }
  }

  // ========== UI Manipulation ==========

  showBanner() {
    this.showElement(this.bannerTarget)
  }

  hidePrompt() {
    this.hideElement(this.bannerTarget)
  }

  showElement(element) {
    if (element) {
      element.classList.remove('d-none')
      element.classList.add('show')
    }
  }

  hideElement(element) {
    if (element) {
      element.classList.add('d-none')
      element.classList.remove('show')
    }
  }

  // ========== Button Management ==========

  getInstallButton() {
    return this.buttonTarget ||
           this.androidInstructionsTarget?.querySelector('button[data-action*="install"]')
  }

  showInstallButton() {
    if (!this.deferredPrompt) return

    const button = this.getInstallButton()
    if (button) {
      this.forceShowButton(button)
    }

    // Double-check after a delay
    setTimeout(() => this.updateInstallButtonVisibility(), 100)
  }

  updateInstallButtonVisibility() {
    const button = this.getInstallButton()
    if (!button) return

    if (this.isAndroid && this.deferredPrompt) {
      this.forceShowButton(button)
    } else if (this.isIOS) {
      this.forceHideButton(button)
    }
  }

  forceShowButton(button) {
    button.removeAttribute('hidden')
    button.classList.remove('d-none')
    button.style.cssText = 'display: inline-block !important; visibility: visible !important;'
  }

  forceHideButton(button) {
    button.style.setProperty('display', 'none', 'important')
  }

  setupAndroidButtonWatcher() {
    // Periodically check if deferredPrompt arrived
    const checkInterval = setInterval(() => {
      if (this.deferredPrompt) {
        this.updateInstallButtonVisibility()
        clearInterval(checkInterval)
      }
    }, this.ANDROID_CHECK_INTERVAL)

    setTimeout(() => clearInterval(checkInterval), this.ANDROID_CHECK_TIMEOUT)

    // Watch for style changes on the button
    const button = this.getInstallButton()
    if (button) {
      const observer = new MutationObserver(() => {
        if (this.deferredPrompt && getComputedStyle(button).display === 'none') {
          this.forceShowButton(button)
        }
      })
      observer.observe(button, { attributes: true, attributeFilter: ['style'] })
    }
  }
}

